import React, { memo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import Checkbox from '@rsos/components/capstone/base/Checkbox';
import { ESRI } from '@rsos/constants/mapProviders';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import {
  GISServiceTypes,
  GISStateSublayer,
  gisSublayerFor,
} from '@rsos/reduxDataStore';
import { updateSublayer } from '@rsos/reduxDataStore';
import { selectMapProvider } from '@rsos/sinatra';
import useIsIRP from '@rsos/utils/useIsIRP';
import { trackToggleSupplementalLayer } from '../../JurisdictionView/Map/gainsight/gisTrackings';
import useToggleGuard from '../useToggleGuard';

const CompactCheckbox = styled(Checkbox)`
  padding: 1px 0;
`;

interface GISSublayerControlProps {
  layerId: string;
  sublayer: GISStateSublayer;
}

/**
 * A checkbox controlling one GISSublayer.
 * Directly toggles visibility on the ArcGIS layer for immediate feedback,
 * then syncs Redux state.
 */
export const GISSublayerControl = memo<GISSublayerControlProps>(
  ({ layerId, sublayer }) => {
    const isDisabled = useSelector(selectMapProvider) !== ESRI;
    const dispatch = useDispatch();
    const { irpVersion } = useIsIRP();

    const toggle = useCallback(() => {
      // Use ArcGIS layer as single source of truth for visibility
      const gisSublayer = gisSublayerFor(sublayer);
      if (!gisSublayer) {
        return;
      }

      // Read current visibility from the layer itself, not Redux
      const newVisible = !gisSublayer.visible;
      gisSublayer.visible = newVisible;

      // Sync Redux state to match
      // Note: Parent layer visibility is handled by GISLayerControl component
      // based on Redux state, not here
      dispatch(
        updateSublayer({
          layerID: layerId,
          sublayerID: sublayer.uniqueId,
          active: newVisible,
        }),
      );

      // Analytics tracking
      const onOffText = newVisible ? 'on' : 'off';
      gspx.trackEvent({
        name: 'map_turn_gis_sublayer',
        type: GPX_EVENT_TYPES.INFO,
        feature: GPX_FEATURES.LEGACY,
        capability: GPX_CAPABILITIES.LEGACY,
        route: GPX_ROUTES.MAP,
        additionalKeys: {
          legacy_category: 'Data Map',
          legacy_name: `Turn ${onOffText} ${sublayer.title}`,
          Category: 'GIS Sublayer',
          'Launched date': new Date(),
          irpVersion,
          sublayerTitle: sublayer.title,
          visibility_state: newVisible ? 'show' : 'hide',
        },
      });

      if (sublayer.service.service_type !== GISServiceTypes.basemap) {
        trackToggleSupplementalLayer({
          layerName: sublayer.title,
          isVisible: newVisible,
          layerCategory: 'gis_sublayer',
        });
      }
    }, [dispatch, layerId, sublayer, irpVersion]);

    const guardedToggle = useToggleGuard(toggle);

    return (
      <CompactCheckbox
        label={sublayer.title}
        isChecked={sublayer.active}
        disabled={isDisabled}
        onChange={guardedToggle}
      />
    );
  },
);
